import { useSearchParams } from "react-router-dom";
import { NotificationHistoryFilter } from "../models/notification-history.model";
import { useMemo } from "react";

export interface NotificationFilterActions {
    setStartDate: (value: Date | undefined) => void;
    setEndDate: (value: Date | undefined) => void;
    setChannelId: (value: number | undefined) => void;
    setStatusId: (value: number | undefined) => void;
    setPageNumber: (value: number) => void;
    setPageSize: (value: number) => void;
    setFilter: (filter: NotificationHistoryFilter) => void;
}

export const useNotificationHistoryFilterParams = () => {
    const [search, setSearch] = useSearchParams();

    const setFilter = (filter: NotificationHistoryFilter) => {
        const query = new URLSearchParams();

        if (filter.startDate)
            query.set('startDate', filter.startDate.toISOString());
        if (filter.endDate)
            query.set('endDate', filter.endDate.toISOString());
        if (filter.channelId !== undefined)
            query.set('channelId', filter.channelId.toString());
        if (filter.statusId !== undefined)
            query.set('statusId', filter.statusId.toString());

        query.set('pageNumber', filter.pageNumber.toString());
        query.set('pageSize', filter.pageSize.toString());

        setSearch(query);
    };

    const setStartDate = (startDate: Date | undefined) => {
        const newSearch = new URLSearchParams(search);
        if (startDate) {
            newSearch.set('startDate', startDate.toISOString());
        } else {
            newSearch.delete('startDate');
        }
        setSearch(newSearch);
    };

    const setEndDate = (endDate: Date | undefined) => {
        const newSearch = new URLSearchParams(search);
        if (endDate) {
            newSearch.set('endDate', endDate.toISOString());
        } else {
            newSearch.delete('endDate');
        }
        setSearch(newSearch);
    };

    const setChannelId = (channelId: number | undefined) => {
        const newSearch = new URLSearchParams(search);
        if (channelId !== undefined) {
            newSearch.set('channelId', channelId.toString());
        } else {
            newSearch.delete('channelId');
        }
        setSearch(newSearch);
    };

    const setStatusId = (statusId: number | undefined) => {
        const newSearch = new URLSearchParams(search);
        if (statusId !== undefined) {
            newSearch.set('statusId', statusId.toString());
        } else {
            newSearch.delete('statusId');
        }
        setSearch(newSearch);
    };

    const setPageNumber = (pageNumber: number) => {
        const newSearch = new URLSearchParams(search);
        newSearch.set('pageNumber', pageNumber.toString());
        setSearch(newSearch);
    };

    const setPageSize = (pageSize: number) => {
        const newSearch = new URLSearchParams(search);
        newSearch.set('pageSize', pageSize.toString());
        setSearch(newSearch);
    };

    const memo = useMemo(() => {
        const startDateParam = search.get('startDate');
        const startDate = startDateParam ? new Date(startDateParam) : undefined;

        const endDateParam = search.get('endDate');
        const endDate = endDateParam ? new Date(endDateParam) : undefined;

        const channelIdParam = search.get('channelId');
        const channelId = channelIdParam ? parseInt(channelIdParam) : undefined;

        const statusIdParam = search.get('statusId');
        const statusId = statusIdParam ? parseInt(statusIdParam) : undefined;

        const pageNumberParam = search.get('pageNumber');
        const pageNumber = pageNumberParam ? parseInt(pageNumberParam) : 1;

        const pageSizeParam = search.get('pageSize');
        const pageSize = pageSizeParam ? parseInt(pageSizeParam) : 30;

        const query: NotificationHistoryFilter = {
            startDate,
            endDate,
            channelId,
            statusId,
            pageNumber,
            pageSize
        };

        return query;
    }, [search]);

    const actions: NotificationFilterActions = {
        setStartDate,
        setEndDate,
        setChannelId,
        setStatusId,
        setPageNumber,
        setPageSize,
        setFilter
    };

    return [memo, actions] as const;
};